import { useState, useEffect, useCallback } from 'react';
import { BadgeCard, BadgeProgress } from '../components/Badges';
import { useToast } from '../components/Toast';
import { apiGet, apiPost } from '../utils/api';

function Badges() {
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({ total_earned: 0, total_available: 0 });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showLocked, setShowLocked] = useState(true);
  const { toast } = useToast();

  const checkForNewBadges = useCallback(async () => {
    try {
      const response = await apiPost('/badges/check');
      
      if (response.ok) {
        const data = await response.json();
        
        // Show celebration toasts for newly earned badges
        if (data.newly_earned && data.newly_earned.length > 0) {
          data.newly_earned.forEach((badge, index) => {
            // Stagger the toasts slightly
            setTimeout(() => {
              toast.badge(`${badge.name}`, badge.emoji);
            }, index * 500);
          });
        }
        
        return data.newly_earned?.length > 0;
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
    return false;
  }, [toast]);

  useEffect(() => {
    const initializeBadges = async () => {
      // First check for any new badges
      const hasNewBadges = await checkForNewBadges();
      
      // Then load all badges
      await loadBadges();
      
      // If new badges were earned, the state will be updated
      if (hasNewBadges) {
        // Reload to get updated list
        await loadBadges();
      }
    };
    
    initializeBadges();
  }, [checkForNewBadges]);

  const loadBadges = async () => {
    try {
      const response = await apiGet('/badges');
      if (response.ok) {
        const data = await response.json();
        setBadges(data.badges || []);
        setStats({
          total_earned: data.total_earned,
          total_available: data.total_available
        });
      }
    } catch (error) {
      console.error('Error loading badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All Badges', emoji: '🏆' },
    { id: 'debt', label: 'Debt', emoji: '💳' },
    { id: 'savings', label: 'Savings', emoji: '💰' },
    { id: 'challenge', label: 'Challenges', emoji: '📨' },
    { id: 'general', label: 'General', emoji: '⭐' }
  ];

  const filteredBadges = badges.filter(badge => {
    if (activeCategory !== 'all' && badge.category !== activeCategory) return false;
    if (!showLocked && !badge.earned) return false;
    return true;
  });

  const earnedCount = badges.filter(b => b.earned).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" data-testid="badges-title">
              🏆 Your Achievements
            </h1>
            <p className="mt-2 text-yellow-100">
              Earn badges by reaching financial milestones!
            </p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 min-w-[200px]">
            <div className="text-center">
              <div className="text-4xl font-bold">{earnedCount}</div>
              <div className="text-sm text-yellow-100">of {stats.total_available} badges earned</div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <BadgeProgress earned={stats.total_earned} total={stats.total_available} />
        
        {stats.total_earned === stats.total_available && (
          <div className="mt-4 text-center p-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg">
            <span className="text-2xl">🌟</span>
            <p className="font-bold text-amber-800">Congratulations! You've earned all badges!</p>
            <p className="text-sm text-amber-600">You're a true Financial Master!</p>
          </div>
        )}
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === cat.id
                ? 'bg-amber-500 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-200 hover:border-amber-300'
            }`}
          >
            <span className="mr-1">{cat.emoji}</span>
            {cat.label}
          </button>
        ))}
        
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showLocked}
              onChange={(e) => setShowLocked(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
            />
            <span className="text-sm text-gray-600">Show locked badges</span>
          </label>
        </div>
      </div>

      {/* Badges Grid */}
      {filteredBadges.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="text-5xl mb-4">🔒</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No badges yet</h3>
          <p className="text-gray-600">Start using the app to earn your first badge!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredBadges.map(badge => (
            <BadgeCard key={badge.id} badge={badge} size="large" />
          ))}
        </div>
      )}

      {/* Tips Section */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4">💡 How to Earn Badges</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">💳</span>
              <span className="font-medium text-gray-900">Debt Badges</span>
            </div>
            <p className="text-gray-600">Pay off your debts using the Debt Snowball Calculator. Each debt paid off brings you closer to freedom!</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">💰</span>
              <span className="font-medium text-gray-900">Savings Badges</span>
            </div>
            <p className="text-gray-600">Make contributions to your savings goals using the Action Engine on your Dashboard.</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">📨</span>
              <span className="font-medium text-gray-900">Challenge Badges</span>
            </div>
            <p className="text-gray-600">Complete Envelope Challenges to gamify your savings and earn exclusive badges!</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Badges;
